import { getIsoConstructionName } from "./construction";
import { RawAddressWithIndices, SovGridContext } from "./types";
import {
  KM_TO_MILES,
  INTEGRATION_NOT_RUN,
  INTEGRATION_FOREIGN,
  INTEGRATION_ZERO_TIV,
  INTEGRATION_NO_MATCH,
  hasNearmapData,
  nearmapRoofMaterial,
} from "./utils";
import { RowSeverity } from "./pivotUtils";
import {
  RC_ITV_BANDS,
  RC_ITV_CHECK,
  RC_ITV_UNDER_INSURED,
  replacementCostItvBand,
} from "./replacementCost";
import { locationClassificationKey } from "../data";
import { isOutOfAppetiteSicCode } from "./sic";
import {
  DEDUCTIBLE_BUCKETS,
  LIMIT_BUCKETS,
  compareDeductibles,
  compareLimits,
  formatDeductibleBin,
  formatLimitBin,
} from "./resolvedTerms";

// Identifies which async data source a field reads from. Pivots over fields
// that depend on a still-loading source would show misleading "UNKNOWN" /
// "N/A (not modeled)" inflation, so the panel renders a spinner instead until
// the source finishes loading. "integration", "limit" and "deductible" stream
// in chunks; "classification" is the single location-classification request.
export type PivotDataSource =
  | "integration"
  | "classification"
  | "limit"
  | "deductible";

export type GroupableField = {
  key: string;
  label: string;
  group: string;
  // Integration data lives on the shared grid context (looked up by
  // address_id) so row identity stays stable while summaries stream in.
  getValue: (
    addr: RawAddressWithIndices,
    context: SovGridContext
  ) => string | undefined | null;
  compare?: (a: string, b: string) => number;
  dependsOn?: PivotDataSource;
  // Optional: tag the rows of a pivot grouped by this field with a severity,
  // computed from the addresses that fell into the row. PivotTable renders
  // the tag as a CSS class (red/yellow tint for tier_1/tier_2). When unset,
  // buildPivotTable skips retaining per-row addresses entirely.
  getRowSeverity?: (
    rowAddresses: RawAddressWithIndices[],
    context: SovGridContext
  ) => RowSeverity | null;
};

// Worst-case wind tier across the addresses in a row: any Tier 1 → red,
// otherwise any Tier 2 → yellow, otherwise no color.
function windTierSeverity(
  rowAddresses: RawAddressWithIndices[],
  context: SovGridContext
): RowSeverity | null {
  let sawTier2 = false;
  for (const addr of rowAddresses) {
    const tier = context.classificationByLocation.get(
      locationClassificationKey(addr.state, addr.county)
    )?.windTier;
    if (tier === "tier_1") return "tier_1";
    if (tier === "tier_2") sawTier2 = true;
  }
  return sawTier2 ? "tier_2" : null;
}

// Once classification has loaded, a location is always present in the map, so
// a null gate means "classified, no gate for this peril" → display "None"
// (rather than letting buildPivotTable bucket it as "UNKNOWN"). A missing
// entry means not-yet-loaded; return undefined so the panel's spinner covers
// it instead.
function windGateValue(
  addr: RawAddressWithIndices,
  context: SovGridContext
): string | undefined {
  const classification = context.classificationByLocation.get(
    locationClassificationKey(addr.state, addr.county)
  );
  if (!classification) return undefined;
  return classification.windGate ?? "None";
}

function eqGateValue(
  addr: RawAddressWithIndices,
  context: SovGridContext
): string | undefined {
  const classification = context.classificationByLocation.get(
    locationClassificationKey(addr.state, addr.county)
  );
  if (!classification) return undefined;
  return classification.eqGate ?? "None";
}

// Flags a SIC-code row red when its code is outside underwriting appetite.
// All addresses in a SIC pivot row share the same code, so any out-of-appetite
// one taints the row.
function sicSeverity(
  rowAddresses: RawAddressWithIndices[]
): RowSeverity | null {
  for (const addr of rowAddresses) {
    if (isOutOfAppetiteSicCode(addr.sic_code)) return "invalid";
  }
  return null;
}

// ---------------------------------------------------------------------------
// Bucketing helpers
// ---------------------------------------------------------------------------

const DIST_TO_COAST_BINS: { lo: number; hi: number; label: string }[] = [
  { lo: 0, hi: 0.1, label: "0 - 0.1 mi" },
  { lo: 0.1, hi: 0.25, label: "0.1 - 0.25 mi" },
  { lo: 0.25, hi: 0.75, label: "0.25 - 0.75 mi" },
  { lo: 0.75, hi: 1.5, label: "0.75 - 1.5 mi" },
  { lo: 1.5, hi: 2, label: "1.5 - 2 mi" },
  { lo: 2, hi: 5, label: "2 - 5 mi" },
  { lo: 5, hi: 10, label: "5 - 10 mi" },
  { lo: 10, hi: 25, label: "10 - 25 mi" },
  { lo: 25, hi: 50, label: "25 - 50 mi" },
  { lo: 50, hi: Infinity, label: "> 50 mi" },
];

function bucketYearBuilt(
  yearStr: string | null | undefined
): string | undefined {
  if (!yearStr) return undefined;
  const year = parseInt(yearStr, 10);
  if (isNaN(year)) return undefined;
  if (year < 1960) return "Pre-1960s";
  return `${Math.floor(year / 10) * 10}s`;
}

function bucketStories(
  storiesStr: string | null | undefined
): string | undefined {
  if (!storiesStr) return undefined;
  const n = parseInt(storiesStr, 10);
  if (isNaN(n)) return undefined;
  if (n <= 1) return "1";
  if (n <= 3) return "2-3";
  if (n <= 10) return "4-10";
  if (n <= 20) return "11-20";
  return "20+";
}

const ROOF_AGE_BINS: { lo: number; hi: number; label: string }[] = [
  { lo: 0, hi: 5, label: "0 - 5 yrs" },
  { lo: 5, hi: 10, label: "5 - 10 yrs" },
  { lo: 10, hi: 15, label: "10 - 15 yrs" },
  { lo: 15, hi: 20, label: "15 - 20 yrs" },
  { lo: 20, hi: Infinity, label: "20+ yrs" },
];

function bucketRoofAge(
  addr: RawAddressWithIndices,
  ctx: SovGridContext
): string | undefined {
  const nm =
    ctx.integrationByAddressId[addr.__extras.fullAddress.address_id]?.nearmap;
  if (!nm) return INTEGRATION_NOT_RUN;
  const age = nm.buildings?.[0]?.roof_age_years;
  if (age == null) return INTEGRATION_NO_MATCH;
  for (const bin of ROOF_AGE_BINS) {
    if (bin.hi === Infinity) {
      if (age >= bin.lo) return bin.label;
    } else if (age >= bin.lo && age < bin.hi) {
      return bin.label;
    }
  }
  return undefined;
}

function bucketDistToCoast(
  addr: RawAddressWithIndices,
  ctx: SovGridContext
): string | undefined {
  const rms =
    ctx.integrationByAddressId[addr.__extras.fullAddress.address_id]?.rms;
  if (!rms) return INTEGRATION_NOT_RUN;
  if (rms.is_foreign_exposure) return INTEGRATION_FOREIGN;
  if (addr.tiv === 0) return INTEGRATION_ZERO_TIV;

  const distKm = rms.location_detail?.locationWsDetail?.distToCoast;
  if (distKm == null) return INTEGRATION_NO_MATCH;

  const distMi = distKm * KM_TO_MILES;
  for (const bin of DIST_TO_COAST_BINS) {
    if (bin.hi === Infinity) {
      if (distMi >= bin.lo) return bin.label;
    } else if (distMi >= bin.lo && distMi < bin.hi) {
      return bin.label;
    }
  }
  return undefined;
}

function getRoofMaterial(
  addr: RawAddressWithIndices,
  ctx: SovGridContext
): string | undefined {
  const data = ctx.integrationByAddressId[addr.__extras.fullAddress.address_id];
  if (!hasNearmapData(data)) return INTEGRATION_NOT_RUN;
  return nearmapRoofMaterial(data) ?? INTEGRATION_NO_MATCH;
}

function getRoofShape(
  addr: RawAddressWithIndices,
  ctx: SovGridContext
): string | undefined {
  const nm =
    ctx.integrationByAddressId[addr.__extras.fullAddress.address_id]?.nearmap;
  if (!nm) return INTEGRATION_NOT_RUN;
  const shapes = nm.buildings?.[0]?.shapes;
  if (!shapes || shapes.length === 0) return INTEGRATION_NO_MATCH;
  return shapes.join(", ");
}

function getCapeRoofCovering(
  addr: RawAddressWithIndices,
  ctx: SovGridContext
): string | undefined {
  const cp =
    ctx.integrationByAddressId[addr.__extras.fullAddress.address_id]?.cape;
  if (!cp) return INTEGRATION_NOT_RUN;
  return cp.primary_structure?.roof_covering ?? INTEGRATION_NO_MATCH;
}

function getCapeRoofGeometry(
  addr: RawAddressWithIndices,
  ctx: SovGridContext
): string | undefined {
  const cp =
    ctx.integrationByAddressId[addr.__extras.fullAddress.address_id]?.cape;
  if (!cp) return INTEGRATION_NOT_RUN;
  return cp.primary_structure?.roof_geometry ?? INTEGRATION_NO_MATCH;
}

function getCapeWildfireHazard(
  addr: RawAddressWithIndices,
  ctx: SovGridContext
): string | undefined {
  const cp =
    ctx.integrationByAddressId[addr.__extras.fullAddress.address_id]?.cape;
  if (!cp) return INTEGRATION_NOT_RUN;
  return cp.wildfire_hazard_potential ?? INTEGRATION_NO_MATCH;
}

function getFloodZone(
  addr: RawAddressWithIndices,
  ctx: SovGridContext
): string | undefined {
  const geo =
    ctx.integrationByAddressId[addr.__extras.fullAddress.address_id]
      ?.geographic_enrichment;
  if (geo == null) return INTEGRATION_NOT_RUN;
  return geo.flood_zone ?? "None";
}

function getEarthquakeZone(
  addr: RawAddressWithIndices,
  ctx: SovGridContext
): string | undefined {
  const geo =
    ctx.integrationByAddressId[addr.__extras.fullAddress.address_id]
      ?.geographic_enrichment;
  if (geo == null) return INTEGRATION_NOT_RUN;
  return geo.earthquake_zone ?? "None";
}

function getKatFemaFloodZone(
  addr: RawAddressWithIndices,
  ctx: SovGridContext
): string | undefined {
  const sk =
    ctx.integrationByAddressId[addr.__extras.fullAddress.address_id]
      ?.spatialkat;
  if (!sk) return INTEGRATION_NOT_RUN;
  if (sk.is_foreign_exposure) return INTEGRATION_FOREIGN;
  if (addr.tiv === 0) return INTEGRATION_ZERO_TIV;
  return sk.fema_flood_zone ?? INTEGRATION_NO_MATCH;
}

function getKatFloodScore(
  addr: RawAddressWithIndices,
  ctx: SovGridContext
): string | undefined {
  const sk =
    ctx.integrationByAddressId[addr.__extras.fullAddress.address_id]
      ?.spatialkat;
  if (!sk) return INTEGRATION_NOT_RUN;
  if (sk.is_foreign_exposure) return INTEGRATION_FOREIGN;
  if (addr.tiv === 0) return INTEGRATION_ZERO_TIV;

  const score = sk.flood_score;
  if (score == null) return INTEGRATION_NO_MATCH;
  return String(score);
}

function getIsoConstruction(addr: RawAddressWithIndices): string | undefined {
  const name = getIsoConstructionName(
    addr.construction_code_type,
    addr.construction_code
  );
  return name ?? undefined;
}

// Bins addresses by the term resolved for one peril. Addresses with no
// resolved term for the peril land in "None". Bins sort by the term values,
// not the label text. All terms in a bin share values, so any one of them
// stands in for the bin when sorting.
function makeResolvedTermField<T>({
  key,
  label,
  group,
  dependsOn,
  getTerm,
  formatBin,
  compareTerms,
}: {
  key: string;
  label: string;
  group: string;
  dependsOn: PivotDataSource;
  getTerm: (addressId: string, ctx: SovGridContext) => T | undefined;
  formatBin: (term: T) => string | undefined;
  compareTerms: (a: T, b: T) => number;
}): GroupableField {
  const termByBin = new Map<string, T>();
  return {
    key,
    label,
    group,
    dependsOn,
    getValue: (addr, ctx) => {
      const term = getTerm(addr.__extras.fullAddress.address_id, ctx);
      if (term === undefined) return "None";
      const bin = formatBin(term);
      if (bin === undefined) return "None";
      termByBin.set(bin, term);
      return bin;
    },
    compare: (a, b) => {
      // A bin with no term is "None". PivotTable's column sort calls this
      // directly, without buildPivotTable's sort-last handling.
      const termA = termByBin.get(a);
      const termB = termByBin.get(b);
      if (termA === undefined || termB === undefined) {
        return (termA === undefined ? 1 : 0) - (termB === undefined ? 1 : 0);
      }
      return compareTerms(termA, termB);
    },
  };
}

const LIMIT_FIELDS: GroupableField[] = LIMIT_BUCKETS.map(({ peril, label }) =>
  makeResolvedTermField({
    key: `limit_${peril}`,
    label,
    group: "Limits",
    dependsOn: "limit",
    getTerm: (addressId, ctx) => ctx.limitByAddressId[addressId]?.[peril],
    formatBin: formatLimitBin,
    compareTerms: compareLimits,
  })
);

const DEDUCTIBLE_FIELDS: GroupableField[] = DEDUCTIBLE_BUCKETS.map(
  ({ peril, label }) =>
    makeResolvedTermField({
      key: `deductible_${peril}`,
      label,
      group: "Deductibles",
      dependsOn: "deductible",
      getTerm: (addressId, ctx) =>
        ctx.deductibleByAddressId[addressId]?.[peril],
      formatBin: formatDeductibleBin,
      compareTerms: compareDeductibles,
    })
);

// ---------------------------------------------------------------------------
// Comparators
// ---------------------------------------------------------------------------

const compareAlpha = (a: string, b: string) => a.localeCompare(b);

const compareDecadeBucket = (a: string, b: string) => {
  const aKey = a === "Pre-1960s" ? "0" : a;
  const bKey = b === "Pre-1960s" ? "0" : b;
  return aKey.localeCompare(bKey);
};

function compareBinOrder(labels: string[]): (a: string, b: string) => number {
  const order = new Map(labels.map((label, i) => [label, i]));
  return (a, b) => (order.get(a) ?? 999) - (order.get(b) ?? 999);
}

const compareDistToCoast = compareBinOrder(
  DIST_TO_COAST_BINS.map((b) => b.label)
);

const compareRoofAge = compareBinOrder(ROOF_AGE_BINS.map((b) => b.label));

const compareFloodScore = (a: string, b: string) => {
  const aNum = Number(a);
  const bNum = Number(b);
  const aIsNum = !isNaN(aNum);
  const bIsNum = !isNaN(bNum);
  if (aIsNum && bIsNum) return aNum - bNum;
  if (aIsNum) return -1;
  if (bIsNum) return 1;
  return a.localeCompare(b);
};

const compareItvBand = compareBinOrder([...RC_ITV_BANDS, INTEGRATION_NOT_RUN]);

function getItvBand(
  addr: RawAddressWithIndices,
  ctx: SovGridContext
): string | undefined {
  const sindri =
    ctx.integrationByAddressId[addr.__extras.fullAddress.address_id]?.sindri;
  return replacementCostItvBand(sindri) ?? INTEGRATION_NOT_RUN;
}

// Tints a band the way the RC ITV marker colors it. Every address in a row
// shares the band, so the first decides.
function itvBandSeverity(
  rowAddresses: RawAddressWithIndices[],
  context: SovGridContext
): RowSeverity | null {
  const [first] = rowAddresses;
  if (!first) return null;
  const band = getItvBand(first, context);
  if (band === RC_ITV_UNDER_INSURED) return "tier_1";
  if (band === RC_ITV_CHECK) return "tier_2";
  return null;
}

// ---------------------------------------------------------------------------
// Groupable fields
// ---------------------------------------------------------------------------

export const GROUPABLE_FIELDS: GroupableField[] = [
  // Location
  {
    key: "state",
    label: "State",
    group: "Location",
    getValue: (addr) => addr.state,
    getRowSeverity: windTierSeverity,
  },
  {
    key: "county",
    label: "County",
    group: "Location",
    getValue: (addr) => addr.county,
    getRowSeverity: windTierSeverity,
  },
  {
    key: "city",
    label: "City",
    group: "Location",
    getValue: (addr) => addr.city,
    getRowSeverity: windTierSeverity,
  },
  {
    // No getRowSeverity: every named wind gate is Tier 1 by construction, so
    // tier tinting would paint every gate row red (and the "None" row red too,
    // via Tier 1 HI/PR/VI or Tier 2 counties that have no gate) — conveying
    // nothing. The grouping itself already expresses the tier-1 split.
    key: "wind_gate",
    label: "Wind Gate",
    group: "Location",
    getValue: (addr, ctx) => windGateValue(addr, ctx),
    dependsOn: "classification",
  },
  {
    key: "eq_gate",
    label: "Earthquake Gate",
    group: "Location",
    getValue: (addr, ctx) => eqGateValue(addr, ctx),
    dependsOn: "classification",
  },
  {
    key: "zip",
    label: "Zip",
    group: "Location",
    getValue: (addr) => addr.zip,
  },
  {
    key: "country",
    label: "Country",
    group: "Location",
    getValue: (addr) => addr.country,
  },

  // Building
  {
    key: "year_built",
    label: "Year Built (Decade)",
    group: "Building",
    getValue: (addr) => bucketYearBuilt(addr.year_built),
    compare: compareDecadeBucket,
  },
  {
    key: "number_of_stories",
    label: "Stories",
    group: "Building",
    getValue: (addr) => bucketStories(addr.number_of_stories),
  },
  {
    key: "construction",
    label: "Construction Type (ISO)",
    group: "Building",
    getValue: (addr) => getIsoConstruction(addr),
  },
  {
    key: "occupancy_description",
    label: "Occupancy",
    group: "Building",
    getValue: (addr) => addr.occupancy_description,
  },
  {
    key: "number_of_buildings",
    label: "# of Buildings",
    group: "Building",
    getValue: (addr) => addr.number_of_buildings,
  },

  // Risk
  {
    key: "sic_code",
    label: "SIC Code",
    group: "Risk",
    getValue: (addr) => addr.sic_code,
    getRowSeverity: sicSeverity,
  },
  {
    key: "sprinkler",
    label: "Sprinkler",
    group: "Risk",
    getValue: (addr) => addr.sprinkler,
  },
  {
    key: "sprinkler_type",
    label: "Sprinkler Type",
    group: "Risk",
    getValue: (addr) => addr.sprinkler_type,
  },
  {
    key: "flood_zone_broker",
    label: "Flood Zone (Broker)",
    group: "Risk",
    getValue: (addr) => addr.flood_zone,
  },
  {
    key: "roof_covering",
    label: "Roof Covering",
    group: "Risk",
    getValue: (addr) => addr.roof_covering,
  },
  {
    key: "ws_cladding_type",
    label: "WS Cladding",
    group: "Risk",
    getValue: (addr) => addr.ws_cladding_type,
  },
  {
    key: "fire_alarm_type",
    label: "Fire Alarm",
    group: "Risk",
    getValue: (addr) => addr.fire_alarm_type,
  },
  {
    key: "burglar_alarm_type",
    label: "Burglar Alarm",
    group: "Risk",
    getValue: (addr) => addr.burglar_alarm_type,
  },

  {
    key: "flood_zone_fema",
    label: "Flood Zone (FEMA)",
    group: "Geo",
    getValue: (addr, ctx) => getFloodZone(addr, ctx),
    dependsOn: "integration",
  },
  {
    key: "earthquake_zone",
    label: "Earthquake Zone",
    group: "Geo",
    getValue: (addr, ctx) => getEarthquakeZone(addr, ctx),
    dependsOn: "integration",
  },
  {
    key: "rms_dist_to_coast",
    label: "RMS Dist. to Coast",
    group: "RMS",
    getValue: (addr, ctx) => bucketDistToCoast(addr, ctx),
    compare: compareDistToCoast,
    dependsOn: "integration",
  },
  {
    key: "katrisk_fema_flood_zone",
    label: "KatRisk FEMA Flood Zone",
    group: "KatRisk",
    getValue: (addr, ctx) => getKatFemaFloodZone(addr, ctx),
    compare: compareAlpha,
    dependsOn: "integration",
  },
  {
    key: "katrisk_flood_score",
    label: "KatRisk Flood Score",
    group: "KatRisk",
    getValue: (addr, ctx) => getKatFloodScore(addr, ctx),
    compare: compareFloodScore,
    dependsOn: "integration",
  },
  {
    key: "nearmap_roof_age",
    label: "Roof Age",
    group: "Nearmap",
    getValue: (addr, ctx) => bucketRoofAge(addr, ctx),
    compare: compareRoofAge,
    dependsOn: "integration",
  },
  {
    key: "nearmap_roof_material",
    label: "Roof Material",
    group: "Nearmap",
    getValue: (addr, ctx) => getRoofMaterial(addr, ctx),
    dependsOn: "integration",
  },
  {
    key: "nearmap_roof_shape",
    label: "Roof Shape",
    group: "Nearmap",
    getValue: (addr, ctx) => getRoofShape(addr, ctx),
    dependsOn: "integration",
  },
  {
    key: "cape_roof_covering",
    label: "Roof Covering",
    group: "Cape Analytics",
    getValue: (addr, ctx) => getCapeRoofCovering(addr, ctx),
    dependsOn: "integration",
  },
  {
    key: "cape_roof_geometry",
    label: "Roof Geometry",
    group: "Cape Analytics",
    getValue: (addr, ctx) => getCapeRoofGeometry(addr, ctx),
    dependsOn: "integration",
  },
  {
    key: "cape_wildfire_hazard",
    label: "Wildfire Hazard",
    group: "Cape Analytics",
    getValue: (addr, ctx) => getCapeWildfireHazard(addr, ctx),
    dependsOn: "integration",
  },
  {
    key: "hh_hurricane",
    label: "HH Hurricane Score",
    group: "HH",
    compare: compareAlpha,
    getValue: (addr, ctx) =>
      ctx.integrationByAddressId[addr.__extras.fullAddress.address_id]
        ?.hazard_hub?.peril_scores?.hurricane,
    dependsOn: "integration",
  },
  {
    key: "hh_flood",
    label: "HH Flood Score",
    group: "HH",
    compare: compareAlpha,
    getValue: (addr, ctx) =>
      ctx.integrationByAddressId[addr.__extras.fullAddress.address_id]
        ?.hazard_hub?.peril_scores?.flood,
    dependsOn: "integration",
  },
  {
    key: "hh_earthquake",
    label: "HH Earthquake Score",
    group: "HH",
    compare: compareAlpha,
    getValue: (addr, ctx) =>
      ctx.integrationByAddressId[addr.__extras.fullAddress.address_id]
        ?.hazard_hub?.peril_scores?.earthquake,
    dependsOn: "integration",
  },
  {
    key: "hh_wildfire",
    label: "HH Wildfire Score",
    group: "HH",
    compare: compareAlpha,
    getValue: (addr, ctx) =>
      ctx.integrationByAddressId[addr.__extras.fullAddress.address_id]
        ?.hazard_hub?.peril_scores?.wildfire,
    dependsOn: "integration",
  },
  {
    key: "hh_wind",
    label: "HH Wind Score",
    group: "HH",
    compare: compareAlpha,
    getValue: (addr, ctx) =>
      ctx.integrationByAddressId[addr.__extras.fullAddress.address_id]
        ?.hazard_hub?.peril_scores?.wind,
    dependsOn: "integration",
  },
  {
    key: "hh_hail",
    label: "HH Hail Score",
    group: "HH",
    compare: compareAlpha,
    getValue: (addr, ctx) =>
      ctx.integrationByAddressId[addr.__extras.fullAddress.address_id]
        ?.hazard_hub?.peril_scores?.hail,
    dependsOn: "integration",
  },
  {
    key: "hh_fire_protection",
    label: "HH Fire Protection Grade",
    group: "HH",
    compare: compareAlpha,
    getValue: (addr, ctx) =>
      ctx.integrationByAddressId[addr.__extras.fullAddress.address_id]
        ?.hazard_hub?.protection?.fire_protection_grade,
    dependsOn: "integration",
  },
  {
    key: "sindri_itv_band",
    label: "RC ITV",
    group: "Luminor Replacement Cost",
    getValue: (addr, ctx) => getItvBand(addr, ctx),
    compare: compareItvBand,
    dependsOn: "integration",
    getRowSeverity: itvBandSeverity,
  },
  ...LIMIT_FIELDS,
  ...DEDUCTIBLE_FIELDS,
];

export function isFieldDataLoading(
  field: GroupableField | undefined,
  context: SovGridContext
): boolean {
  if (!field?.dependsOn) return false;
  if (field.dependsOn === "integration") {
    return context.integrationStatus.isLoading;
  }
  if (field.dependsOn === "classification") {
    return context.classificationStatus.isLoading;
  }
  if (field.dependsOn === "limit") {
    return context.limitStatus.isLoading;
  }
  if (field.dependsOn === "deductible") {
    return context.deductibleStatus.isLoading;
  }
  return false;
}
